import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { ContentBox } from '@src/elements/content-box/content-box'
import { Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { ActionBar } from '@src/components/action-bar/action-bar'
import { Breadcrumbs } from '@src/elements/breadcrumbs/breadcrumbs'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { Modal } from '@src/elements/modal/modal'

import { isEmailValid } from '@src/util/validation'
import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import { track } from '@src/util/functions'
import camelcaseKeys from 'camelcase-keys'

import Member from '@src/data/member'

import './connect-mobile-member.sass'

enum ErrorType {
  CODE = 'code_invalid',
  EMAIL = 'email_invalid'
}

class ErrorResponse {
  errors: ErrorType[]

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

type MemberResponse = { member: Member }

@Component
export class ConnectMobileMember extends Vue {

  email: string = ''
  code: string = ''

  protected apiClient: ApiClient = new ApiClient()
  protected errorCodes: string[] = []
  protected showConfirmPopup: boolean = false

  constructor () {
    super()
    track('/members/connect-mobile')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER')} - ${this.$root.$t('ADD')}`
  }

  beforeDestroy () {
    this.$store.dispatch('closeToast')
  }

  render (h: CreateElement) {
    return <div class='connect-mobile-member'>
      <ActionBar>
        <Breadcrumbs clickAction={this.goToNewUser} crumbs={[
          <div ref='goto-new-user'>{this.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER')}</div>
        ]} />
        <Subtitle2>{this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.CONNECTING_ACCOUNTS')}</Subtitle2>
      </ActionBar>
      <ContentBox>
        <div slot='content'>
          <Subtitle1>{this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.MOBILE_ACCOUNT_MESSAGE')}</Subtitle1>
          <div class='row'>
            <div>
              <Input ref='email' value={this.email}
                     valueChanged={(value: string) => this.email = value} label={this.$t('EMAIL') + '*'} />
              <InputFootnote class='error'>{this.hasError('email') ? this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.VALID_EMAIL') : null}</InputFootnote>
            </div>
          </div>

          <div class='row'>
            <div>
              <Input ref='qr-code' value={this.code}
                     valueChanged={(value: string) => this.code = value} label={this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.NEW_QR_CODE') + '*'} />
              <InputFootnote class='error'>{this.hasError('code') ? this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.VALID_CODE') : null}</InputFootnote>
            </div>
          </div>

        </div>
        <div slot={'bottom'}>
          <Button disabled={this.checkIfDisabled()} type='elevated' class='connect-btn' label={this.$t('DONE')} click={this.attemptConnectMobileMember} />
        </div>
      </ContentBox>
      <Modal class='confirmation-modal' show={this.showConfirmPopup} closeCallback={this.invertShowConfirmationPopup}
             title={this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.CONNECTING_ACCOUNTS')}>
        {this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.CONNECTING_ACCOUNTS_DETAILS')}
        <div class='btn-row' slot='footer'>
          <Button type='outline' label={this.$t('CANCEL')} click={this.invertShowConfirmationPopup} ref='closeBtn' />
          <Button type='elevated' class='save-btn' label={this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.CONNECT')} click={this.connectMember} ref='saveBtn' />
        </div>
      </Modal>
    </div>
  }

  protected invertShowConfirmationPopup () {
    this.showConfirmPopup = !this.showConfirmPopup
  }

  protected checkIfDisabled () {
    return !this.email || !this.code
  }

  protected goToNewUser () {
    this.$router.push('/trainers/platform/members/new')
  }

  protected goToUserAccount (id: number) {
    this.$router.push('/trainers/platform/members/' + id + '/status')
  }

  protected validateForm () {
    let errorCodes: string[] = []

    if (!isEmailValid(this.email!)) {
      errorCodes.push('email')
    }

    this.errorCodes = errorCodes
  }

  protected hasError (fieldName: string): boolean {
    return !!this.errorCodes.find(err => fieldName === err)
  }

  protected async attemptConnectMobileMember () {
    this.validateForm()
    if (this.errorCodes.length === 0) {
      this.invertShowConfirmationPopup()
    }
  }

  protected connectMember () {

    return this.apiClient.post(API.TRAINERS_API.MEMBERS_CONNECT_MOBILE, { member: { email: this.email, code: this.code } })
      .then((response: MemberResponse) => {
        const member = new Member(camelcaseKeys(response.member))
        this.$store.dispatch('showToast', {
          fadeOut: false,
          message: this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.SUCCESSFULLY_CONNECTING_ACCOUNTS'),
          actionText: 'Go to user account',
          actionCallback: () => this.goToUserAccount(member.id)
        })
      })
      .catch(error => {
        this.setErrors(error.status, new ErrorResponse(error.errorData))
      }).finally(() => {
        this.invertShowConfirmationPopup()
      })
  }

  protected setErrors (statusCode, errorResponse) {

    if (statusCode === 403) {
      this.$store.dispatch('showToast', {
        message: this.$t('PAGE.MEMBERS.CONNECT_MOBILE_MEMBER.ALREADY_ASSIGNED'),
        fadeOut: false
      })
    }
    if (statusCode === 422 && errorResponse.errors) {
      if (errorResponse.errors.find(err => err === ErrorType.EMAIL)) {
        this.errorCodes.push('email')
      }
      if (errorResponse.errors.find(err => err === ErrorType.CODE)) {
        this.errorCodes.push('code')
      }
    }
  }
}
